/**
 * ToastView — presentation only.
 *
 * Hard rule: keep the {@link ToastViewProps} shape frozen. `butterToast.tsx`
 */
import { toast as sonner } from 'sonner'

import { CheckedIcon, CloseIcon, ErrorIcon, InfoIcon } from '#icons'
import type { IconProps } from '#types'
// Import cn from its file, not the `#utils` barrel: the barrel re-exports
// clipboard.ts, which now imports butterToast → would form a utils↔patterns cycle.
import { cn } from '../../utils/cn'

import type { ToastVariant, ToastViewProps } from './types'

const VARIANT_ICON: Record<ToastVariant, (props: IconProps) => React.ReactElement> = {
  info: InfoIcon,
  success: CheckedIcon,
  error: ErrorIcon,
}

export function ToastView({
  id,
  variant,
  title,
  message,
  dismissible = true,
  action,
}: ToastViewProps) {
  const Icon = VARIANT_ICON[variant]

  return (
    <div
      data-variant={variant}
      role={variant === 'error' ? 'alert' : 'status'}
      className={cn(
        // items-center so the tile stays optically balanced when the message wraps.
        'group flex w-full items-center gap-2.5 rounded-xl border border-butter-gray-100 bg-white p-3 shadow-dropshadow-50',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-lg',
          'group-data-[variant=info]:bg-butter-blue-25 group-data-[variant=info]:text-butter-blue-500',
          'group-data-[variant=success]:bg-butter-success-200 group-data-[variant=success]:text-butter-success-600',
          'group-data-[variant=error]:bg-butter-error-100 group-data-[variant=error]:text-butter-error-500',
        )}
      >
        <Icon className="size-6.5" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-lg font-semibold leading-tight text-butter-gray-900">{title}</p>
        {message ? (
          <p className="text-sm font-medium leading-snug text-butter-gray-600">{message}</p>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={action.onClick}
            className="text-interactive:text-butter-gray-900 mt-1 self-start text-sm font-semibold text-butter-gray-700"
          >
            {action.label}
          </button>
        ) : null}
      </div>

      {dismissible ? (
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => sonner.dismiss(id)}
          // 24px box: renders Figma's 12px X while meeting the 24px minimum hit area.
          // self-start keeps the X at the top edge while the icon tile centers.
          className="text-interactive:text-butter-gray-900 flex size-6 shrink-0 self-start items-center justify-center rounded text-butter-gray-600"
        >
          <CloseIcon className="size-6" strokeWidth={1.5} />
        </button>
      ) : null}
    </div>
  )
}